import type { TLNodeExecutionError, TLWfTriggerType } from '@/api/gen/tessera-backend-api.schemas';

// Moved to `@/utils/workflow-execution` so non-loom features can use them without importing
// across feature directories. Re-exported here only so loom's existing call sites keep working;
// TODO: point loom's imports at the shared module and drop this line.
export {
  EXECUTION_STATUS_CONFIG,
  formatExecutionError,
  isNonTerminalExecutionStatus,
  NON_TERMINAL_EXECUTION_STATUSES,
} from '@/utils/workflow-execution';

export const TRIGGER_TYPE_LABELS: Record<TLWfTriggerType, string> = {
  MANUAL: 'Manual',
  SCHEDULED: 'Scheduled',
  RETRY: 'Retry',
};

export enum ExecutionStartedWithin {
  LAST_24_HOURS = '24h',
  LAST_7_DAYS = '7d',
  LAST_30_DAYS = '30d',
}

export const STARTED_WITHIN_LABELS: Record<ExecutionStartedWithin, string> = {
  [ExecutionStartedWithin.LAST_24_HOURS]: 'Last 24 hours',
  [ExecutionStartedWithin.LAST_7_DAYS]: 'Last 7 days',
  [ExecutionStartedWithin.LAST_30_DAYS]: 'Last 30 days',
};

const STARTED_WITHIN_MS: Record<ExecutionStartedWithin, number> = {
  [ExecutionStartedWithin.LAST_24_HOURS]: 24 * 60 * 60 * 1000,
  [ExecutionStartedWithin.LAST_7_DAYS]: 7 * 24 * 60 * 60 * 1000,
  [ExecutionStartedWithin.LAST_30_DAYS]: 30 * 24 * 60 * 60 * 1000,
};

/** UTC ISO cutoff for the `started_after` API filter of a started-within window. */
export function startedWithinCutoffIso(within: ExecutionStartedWithin, nowMs: number = Date.now()): string {
  return new Date(nowMs - STARTED_WITHIN_MS[within]).toISOString();
}

function parseDateParam(date: string): [number, number, number] {
  const [year, month, day] = date.split('-').map(Number);
  return [year, month - 1, day];
}

/** UTC ISO of local midnight starting a `YYYY-MM-DD` date, for `started_after`. */
export function localDateStartIso(date: string): string {
  const [year, monthIndex, day] = parseDateParam(date);
  return new Date(year, monthIndex, day).toISOString();
}

/** UTC ISO of the last local millisecond of a `YYYY-MM-DD` date, for `started_before`. */
export function localDateEndIso(date: string): string {
  const [year, monthIndex, day] = parseDateParam(date);
  return new Date(year, monthIndex, day, 23, 59, 59, 999).toISOString();
}

/** Local-midnight Date of a `YYYY-MM-DD` range-bound param. */
export function dateParamToLocalDate(date: string): Date {
  const [year, monthIndex, day] = parseDateParam(date);
  return new Date(year, monthIndex, day);
}

/** Local `YYYY-MM-DD` of a Date, the URL-param shape of the custom range bounds. */
export function toDateParam(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** True for a `YYYY-MM-DD` string naming a real calendar date — rejects Date-rollover inputs like `2026-99-99`. */
export function isValidDateParam(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [year, monthIndex, day] = parseDateParam(date);
  const parsed = new Date(year, monthIndex, day);
  return parsed.getFullYear() === year && parsed.getMonth() === monthIndex && parsed.getDate() === day;
}

export { formatDuration } from '@/utils/duration';

/** A node's run error as text — structured errors arrive as objects and read as pretty JSON. */
export function nodeExecutionErrorText(error: TLNodeExecutionError | undefined): string | null {
  if (error === null || error === undefined) return null;
  return typeof error === 'string' ? error : JSON.stringify(error, null, 2);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/** Epoch ms for a backend timestamp (naive UTC — no zone suffix on the wire). */
export function parseUtcMs(iso: string): number {
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime();
}

/**
 * Whole-unit countdown ("9d 23h 59m", "1h 04m 05s", "4m 12s", "38s"),
 * clamped at zero. Seconds are dropped at day scale to keep the label
 * compact; everything below a day ticks per second.
 */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.round(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}
